import {FormEvent, useCallback, useMemo, useState} from "react";
import {styled} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@memaro/components/Button.tsx";
import Form from "@memaro/components/Form.tsx";
import Alert from "@mui/material/Alert";
import {useAuth} from "../lib/auth.ts";
import {
    InvalidPasswordException,
    PasswordsDoNotMatch,
    nameFromError,
} from "@memaro/auth";
import {useFormInput} from "@memaro/forms";
import {useRouter} from "../hooks/useRouter";
import {useFeedback} from "@memaro/feedback";
import {unauthenticated} from "../hoc/auth.tsx";

const StyledStack = styled(Stack)({
    width: 'fit-content',
    margin: '0 auto'
});

const StyledTextField = styled(TextField)({
    marginTop: 30,
    minWidth: 300,
    fontFamily: "inherit"
})

const RegisterConfirmPage = unauthenticated(() => {
    const {gotoLogin} = useRouter();
    const {signUpUsername, confirmSignUp} = useAuth();
    const {success} = useFeedback()

    const [email, setEmail] = useFormInput(signUpUsername)
    const [confirmationCode, setConfirmationCode] = useFormInput()
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    const handleRegisterConfirm = useCallback(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true)
        try {
            await confirmSignUp({
                username: email,
                confirmationCode
            });
            success?.("Uw account is succesvol geverifieerd. U kunt nu inloggen.")
            gotoLogin()
        } catch (e: unknown) {
            setError(nameFromError(e))
        } finally {
            setLoading(false)
        }
    }, [confirmSignUp, email, confirmationCode, success, gotoLogin])

    const errorText = useMemo(() => {
        switch (error) {
            case "":
                return ""
            case PasswordsDoNotMatch:
                return "Opgegeven wachtwoorden komen niet overeen"
            case InvalidPasswordException:
                return "Wachtwoord voldoet niet aan alle eisen"
            default:
                console.error("Unknown error:", error)
                return "Onbekende fout opgetreden"
        }
    }, [error])

    return (
        <StyledStack>
            <Form onSubmit={handleRegisterConfirm}>
                <h1>Verifieer registratie</h1>
                {errorText ? <Alert severity="error">{errorText}</Alert> : null}
                <StyledTextField label="Email adres" autoComplete="username" value={email} onChange={setEmail} />
                <StyledTextField label="Activeringscode" value={confirmationCode} onChange={setConfirmationCode} autoFocus />
                <Box sx={{display: "flex", flexDirection: "row-reverse",}}>
                    <Box>
                        <Button type="submit" color="primary" sx={{marginTop: "10px", marginLeft: "10px"}} loading={loading}>Verifieren</Button>
                    </Box>
                </Box>
            </Form>
        </StyledStack>
    )
})

export default RegisterConfirmPage
